import React, { useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { ButtonTertiary } from "src/UI/ButtonTertiary";
import MediaLibraryPickerCst from "@components/MediaLibraryPickerCst";
import { useMediaUpload } from "@components/MediaLibraryPickerCst/hooks/useMediaUpload";
import { LearningResourceType, MediaType } from "src/interfaces/Media";
import { WizardStepProps } from "@components/WizardCst/types";
import { ActivityWizardData } from "../../../../types";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import { useWizardReadOnly } from "@components/WizardCst";

interface PackageContentProps extends WizardStepProps<ActivityWizardData> {
  locked?: boolean;
}

const PackageContent: React.FC<PackageContentProps> = ({
  data,
  setData,
  errors,
  locked: lockedProp = false,
}) => {
  const readOnly = useWizardReadOnly();
  const locked = lockedProp || readOnly;
  const { t } = useTranslation("wizard-activity");
  const { t: tPicker } = useTranslation("media-library-picker");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const { can } = usePermissions();
  const canManageMedia = can([PERMISSIONS.media.read, PERMISSIONS.media.write]);
  const removeDisabled = locked || !canManageMedia;

  const isXapi = data.type === "DA_XAPI";
  const fieldKey = isXapi ? "xapi_package_id" : "scorm_package_id";
  const learningResourceType = isXapi
    ? LearningResourceType.XAPI
    : LearningResourceType.SCORM;
  const current = data[fieldKey];
  const error = errors[fieldKey];

  const [pickerOpen, setPickerOpen] = useState(false);

  const {
    inputProps,
    openFileDialog,
    isUploading,
    uploadError,
    clearUploadError,
  } = useMediaUpload({
    corporateId,
    mediaType: MediaType.LEARNING_RESOURCE,
    learningResourceType,
    enabled: !locked && canManageMedia,
    onUploaded: (item) =>
      setData({ [fieldKey]: item } as Partial<ActivityWizardData>),
  });

  const handlePickFromLibrary = () => {
    if (!canManageMedia) return;
    setPickerOpen(true);
  };

  const handlePickerClose = () => {
    setPickerOpen(false);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearUploadError();
    setData({ [fieldKey]: null } as Partial<ActivityWizardData>);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          padding: "16px",
          borderRadius: "8px",
          border: error ? `1px solid ${theme.palette.error.main}` : "none",
          backgroundColor: theme.palette.background.card || "#2A2A2A",
        }}
      >
        {isUploading ? (
          <>
            <CircularProgress size={20} />
            <Typography
              sx={{ fontSize: "14px", color: theme.palette.text.primary }}
            >
              {t("content.package.uploading")}
            </Typography>
          </>
        ) : (
          <>
            <ButtonSecondary
              size="md"
              onClick={handlePickFromLibrary}
              disabled={locked || !canManageMedia}
            >
              {t("content.package.pick-from-library")}
            </ButtonSecondary>
            <ButtonTertiary
              size="md"
              onClick={openFileDialog}
              startIcon={<Upload size={16} />}
              disabled={locked || !canManageMedia}
            >
              {t("content.package.upload-new")}
            </ButtonTertiary>
          </>
        )}
        <input {...inputProps} />
      </Box>

      {uploadError && (
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.error.main,
            mt: 0.5,
            ml: 1,
          }}
        >
          {uploadError}
        </Typography>
      )}

      {current && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            padding: "10px 14px",
            borderRadius: "8px",
            backgroundColor: theme.palette.background.card || "#2A2A2A",
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              color: theme.palette.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {current.label || `#${current.id}`}
            <Box
              component="span"
              sx={{ color: theme.palette.text.tertiary, ml: 1 }}
            >
              {`| ID: ${current.id}`}
            </Box>
          </Typography>
          <Box
            onClick={removeDisabled ? undefined : handleRemove}
            sx={{
              cursor: removeDisabled ? "not-allowed" : "pointer",
              display: "flex",
              color: theme.palette.text.tertiary,
              opacity: removeDisabled ? 0.4 : 1,
              "&:hover": {
                color: removeDisabled
                  ? theme.palette.text.tertiary
                  : theme.palette.error.main,
              },
            }}
          >
            <X size={16} />
          </Box>
        </Box>
      )}

      {error && (
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.error.main,
            mt: 0.5,
            ml: 1,
          }}
        >
          {t(`errors.${error}`)}
        </Typography>
      )}

      <MediaLibraryPickerCst
        open={pickerOpen}
        onClose={handlePickerClose}
        title={
          isXapi
            ? tPicker("title-xapi") || "Aggiungi pacchetto xAPI"
            : tPicker("title-scorm") || "Aggiungi pacchetto SCORM"
        }
        mediaType={MediaType.LEARNING_RESOURCE}
        learningResourceType={learningResourceType}
        multiple={false}
        initialSelection={
          current
            ? { id: Number(current.id), label: current.label || "" }
            : null
        }
        onConfirm={(selection) => {
          clearUploadError();
          setData({ [fieldKey]: selection } as Partial<ActivityWizardData>);
        }}
      />
    </Box>
  );
};

export default PackageContent;
